/**
 * Platform detection helpers. All platform branching across the app should
 * go through this module so that swapping to Capacitor later only requires
 * replacing implementations here — not changing every caller.
 */

/** True when running inside an installed PWA (Android/desktop) or iOS home-screen app. */
export function isPWA(): boolean {
  if (typeof window === "undefined") return false;
  // iOS Safari uses navigator.standalone; everyone else uses display-mode media query.
  const standaloneMedia = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return standaloneMedia || iosStandalone;
}

/** True when wrapped by Capacitor. Always false today; flips on automatically once Capacitor is added. */
export function isNative(): boolean {
  if (typeof window === "undefined") return false;
  // Capacitor sets window.Capacitor when running in a native shell.
  return Boolean((window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.());
}

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream;
}

export function isIOSSafari(): boolean {
  if (!isIOS()) return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export function isAndroid(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

/** True for browsers that support the BeforeInstallPromptEvent (Chromium-based). */
export function supportsInstallPrompt(): boolean {
  if (typeof window === "undefined") return false;
  return "onbeforeinstallprompt" in window;
}
